import React from 'react';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';

import { useSelector } from 'hooks';
import useQrCodeScanner from 'hooks/useQrCodeScanner';
import { CONTEXT_POLLING_INTERVAL } from 'constants/index';
import { useGetContextQuery } from 'modules/dealers/service';

import { displayOriginSelection, hasErrorOnKiosk, isKioskOrigin } from 'modules/dealers/selectors';

import Header from './Header';
import Background from './Background';
import KioskWarningModal from './KioskWarningModal';
import RemoteKioskError from '../Errors/RemoteKioskError';
import SelfServiceTypeButtons from './SelfServiceTypeButtons';
import SelfServiceOriginButtons from './SelfServiceOriginButtons';
import useLoginRedirection, { State } from './useLoginRedirection';

import './index.css';

export type HomeButtonProps = {
  onClick: (state: State) => void,
};

const Home: React.FC = () => {
  useQrCodeScanner();

  const hasKioskError = useSelector(hasErrorOnKiosk);
  const displayOriginButtons = useSelector(displayOriginSelection);
  const isKiosk = useSelector(isKioskOrigin);

  const [showPopup, setShowPopup] = React.useState(false);

  const handleClick = useLoginRedirection({ onError: setShowPopup });

  const { data: contextData, isLoading } = useGetContextQuery(undefined, {
    pollingInterval: CONTEXT_POLLING_INTERVAL,
  });

  const {
    dealerName, brand, type, kioskName, kioskBackgroundMedia, backgroundMedia, portraitBackgroundMedia,
  } = contextData ?? {};

  const homeTitle = (!displayOriginButtons && kioskName) || dealerName;

  // Kiosk rendering must never vary with orientation (BMWVID-29170): a single asset is
  // used for both slots, so a kiosk without its own kioskBackgroundMedia keeps falling
  // back to the landscape backgroundMedia regardless of the viewport's orientation.
  const kioskMedia = kioskBackgroundMedia ?? backgroundMedia;

  return (
    <Background
      loading={isLoading}
      landscapeMedia={isKiosk ? kioskMedia : backgroundMedia}
      portraitMedia={isKiosk ? kioskMedia : portraitBackgroundMedia}
    >
      <Header className="header-home" />
      <div className="flex flex-col justify-between grow w-full max-w-7xl z-30">
        <div className="w-full max-w-7xl m-auto text-white mt-8 grow sm:grow-0 px-7">
          <div className="flex items-center flex-col">
            {brand?.logoPathMobile && (
              <div className="size-logo-home-container md:size-logo-home-container-md" data-testid="logo">
                <div className="bg-logo-home p-logo-home-padding size-full flex mask-image">
                  <img src={brand.logoPathMobile} alt={`${brand.name} logo`} className="m-auto logo-home" />
                </div>
              </div>
            )}
            {homeTitle && (
              <h1
                className={classNames('home [filter:var(--filter-home-title)] [opacity:var(--opacity-home-subtitle)] uppercase font-bold', {
                  'truncate w-full': kioskName,
                })}
              >
                {homeTitle}
              </h1>
            )}
            <h2 className="home [filter:var(--filter-home-title)]">
              <FormattedMessage
                id="home.title"

                defaultMessage="{type, select, CHECK_IN {Ready for your check-in} CHECK_OUT {Ready for your check-out} other {How can we help you} }?"
                values={{ type }}
              />
            </h2>
          </div>
        </div>

        {displayOriginButtons && <SelfServiceOriginButtons onClick={handleClick} />}
        {!displayOriginButtons && (
          <>
            {hasKioskError && <RemoteKioskError />}
            {!hasKioskError && <SelfServiceTypeButtons onClick={handleClick} />}
          </>
        )}

        {showPopup && <KioskWarningModal onClose={setShowPopup} />}
      </div>
    </Background>
  );
};

export default Home;
